import { useTranslation } from 'react-i18next';
import { getLocale } from '../../pages/DashboardPage';
import { categoryLabel, expiryState, formatMonth, EXPIRY_STYLE, STATUS_STYLE } from '../../lib/service';

// Huoltokirjaukset (#82): huollon tila, tarvikkeiden nykytila ja tapahtumahistoria
// lukunäkymän osina. Käytössä Huolto-sivulla ja suojatilakortilla (myös Admin, jos
// Superadmin on sallinut asiakkaalle huoltohistorian näkyvyyden).

export function formatDate(value, lang) {
  return value ? new Date(value).toLocaleDateString(getLocale(lang)) : '—';
}

export function StatusBadge({ status }) {
  const { t } = useTranslation();
  if (!status) return <span className="text-gray-400">—</span>;
  return (
    <span className={`inline-block text-xs font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE[status] || 'bg-gray-100 text-gray-700'}`}>
      {t(`service.status.${status}`)}
    </span>
  );
}

export function ExpiryText({ value }) {
  const { t } = useTranslation();
  if (!value) return <span className="text-gray-400">—</span>;
  const state = expiryState(value);
  return (
    <span className={EXPIRY_STYLE[state] || 'text-gray-800'}>
      {formatMonth(value)}
      {state === 'expired' && <> · {t('service.expired')}</>}
    </span>
  );
}

export function ItemsTable({ items, renderActions }) {
  const { t } = useTranslation();
  if (!items.length) return <p className="text-sm text-gray-400">{t('service.noItems')}</p>;
  return (
    <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
      {items.map(item => (
        <li key={item.id} className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <div className="flex-1 min-w-[10rem]">
            <p className="font-medium text-gray-800">{item.name}</p>
            {(categoryLabel(item, t) || item.model) && (
              <p className="text-xs text-gray-500">{[categoryLabel(item, t), item.model].filter(Boolean).join(' · ')}</p>
            )}
          </div>
          <div className="w-24 text-gray-700">
            <span className="text-xs text-gray-500">{t('service.quantity')} </span>
            {item.quantity != null ? `${Number(item.quantity)}${item.unit ? ` ${item.unit}` : ''}` : '—'}
          </div>
          {/* Vanhenemiskuukausi aina näkyvissä otsikoituna (päätös 37) */}
          <div className="w-44">
            <span className="text-xs text-gray-500">{t('service.expiresOn')} </span>
            {item.expires_on ? <ExpiryText value={item.expires_on} /> : <span className="text-gray-400">{t('service.expiryNotSet')}</span>}
          </div>
          {item.marked_for_replacement && (
            <span className="text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">{t('service.markedForReplacement')}</span>
          )}
          {renderActions && <div className="w-full sm:w-auto">{renderActions(item)}</div>}
        </li>
      ))}
    </ul>
  );
}

function itemText(entry, names, t) {
  const p = entry.payload || {};
  const name = names.get(p.item_id) || p.name || '—';
  const parts = [];
  if (p.quantity != null && entry.type !== 'item_remove') parts.push(`${t('service.quantity')} ${p.quantity}${p.unit ? ` ${p.unit}` : ''}`);
  else if (p.quantity === null && entry.type === 'item_update') parts.push(`${t('service.quantity')} —`);
  if (p.model !== undefined && entry.type !== 'item_remove') parts.push(`${t('service.model')} ${p.model ?? '—'}`);
  if (p.expires_on !== undefined && entry.type !== 'item_remove') parts.push(`${t('service.expiresOn')} ${p.expires_on ? formatMonth(p.expires_on) : '—'}`);
  if (p.name !== undefined && entry.type === 'item_update') parts.unshift(`${t('service.name')} ${p.name}`);
  return `${name}${parts.length ? ` · ${parts.join(', ')}` : ''}`;
}

function ServiceEntryBody({ entry }) {
  const { t } = useTranslation();
  const p = entry.payload || {};
  const notes = p.notes ?? p.description;
  const flagged = (p.checks || []).filter(c => c.status !== 'ok' || c.note);
  return (
    <div className="space-y-1">
      {notes && <p className="text-gray-700 whitespace-pre-line">{notes}</p>}
      {flagged.length > 0 && (
        <ul className="text-xs text-gray-600 space-y-0.5">
          {flagged.map((c, i) => (
            <li key={c.key || i}>
              <span className={c.status === 'fault' ? 'text-red-700 font-medium' : ''}>{c.label}: {t(`service.checkStatus.${c.status}`)}</span>
              {c.note && <> · {c.note}</>}
            </li>
          ))}
        </ul>
      )}
      {p.checks?.length > 0 && flagged.length === 0 && (
        <p className="text-xs text-gray-500">{t('service.allChecksOk', { count: p.checks.length })}</p>
      )}
    </div>
  );
}

export function EntryList({ entries, items = [] }) {
  const { t, i18n } = useTranslation();
  if (!entries.length) return <p className="text-sm text-gray-400">{t('service.noEntries')}</p>;
  // Nimet myös jo poistetuille riveille lisäystapahtumista
  const names = new Map(items.map(i => [i.id, i.name]));
  for (const e of entries) {
    if (e.type === 'item_add' && e.payload?.name && !names.has(e.payload.item_id)) names.set(e.payload.item_id, e.payload.name);
  }
  return (
    <ul className="divide-y divide-gray-100">
      {entries.map(e => (
        <li key={e.id} className="py-2 text-sm">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-gray-500 text-xs">
              {new Date(e.performed_at).toLocaleString(getLocale(i18n.language), { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="font-medium text-gray-800">{t(`service.type.${e.type}`)}</span>
            {e.type === 'service' && <StatusBadge status={e.payload?.status} />}
            {e.recorded_offline && <span className="text-xs text-gray-400">{t('service.offlineEntry')}</span>}
            {e.pending && <span className="text-xs text-amber-600">{t('service.pending')}</span>}
          </div>
          {e.type === 'service' ? <ServiceEntryBody entry={e} /> : <p className="text-gray-700">{itemText(e, names, t)}</p>}
          <p className="text-xs text-gray-400">
            {e.performed_by_email || ''}
            {e.applied === false && <> · {t('service.notApplied')}</>}
          </p>
        </li>
      ))}
    </ul>
  );
}
